// Альтернативные методы кеширования (canvas, fetch)
import { SizeEstimator } from './SizeEstimator.js';

export class AlternativeCacheMethods {
    constructor(imageCacheDB, cacheManager) {
        this.imageCacheDB = imageCacheDB;
        this.cacheManager = cacheManager;
        this.sizeEstimator = new SizeEstimator();
    }

    /**
     * Кеширование через canvas (обходит CORS)
     */
    async cacheImageViaCanvas(postcard, imageUrl) {
        return new Promise((resolve) => {
            // Проверяем тип файла
            const isVideo = imageUrl.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i);
            const isGif = imageUrl.match(/\.gif$/i);
            
            if (isVideo) {
                // Для видео используем fetch
                this.cacheVideoViaFetch(postcard, imageUrl).then(resolve);
                return;
            }
            
            if (isGif) {
                // Для GIF используем fetch (canvas не сохраняет анимацию)
                this.cacheGifViaFetch(postcard, imageUrl).then(resolve);
                return;
            }
            
            // Для обычных изображений используем canvas
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = async () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    
                    ctx.drawImage(img, 0, 0);
                    
                    canvas.toBlob(async (blob) => {
                        if (blob) {
                            // Кешируем в IndexedDB
                            await this.imageCacheDB.saveImage(imageUrl, blob, {
                                url: imageUrl,
                                postcardId: postcard.id,
                                originalUrl: imageUrl,
                                method: 'canvas',
                                type: 'image'
                            });
                            
                            resolve(blob);
                        } else {
                            resolve(null);
                        }
                    }, 'image/jpeg', 0.9);
                } catch (error) {
                    console.log('Canvas caching error:', error);
                    resolve(null);
                }
            };
            
            img.onerror = () => {
                resolve(null);
            };
            
            img.src = imageUrl;
        });
    }

    /**
     * Кеширование видео через fetch
     */
    async cacheVideoViaFetch(postcard, videoUrl) {
        try {
            const response = await fetch(videoUrl, {
                mode: 'cors',
                credentials: 'omit'
            });
            
            if (!response.ok) {
                return null;
            }
            
            const videoBlob = await response.blob();
            
            // Кешируем в Cache API
            await this.cacheManager.cacheVideo(videoUrl, videoBlob);
            
            // Кешируем в IndexedDB
            await this.imageCacheDB.saveImage(videoUrl, videoBlob, {
                url: videoUrl,
                postcardId: postcard.id,
                originalUrl: videoUrl,
                method: 'fetch',
                type: 'video',
                mimeType: videoBlob.type,
                realSize: videoBlob.size
            });
            
            return videoBlob;
        } catch (error) {
            return null;
        }
    }

    /**
     * Кеширование GIF через fetch
     */
    async cacheGifViaFetch(postcard, gifUrl) {
        try {
            const response = await fetch(gifUrl, {
                mode: 'cors',
                credentials: 'omit'
            });
            
            if (!response.ok) {
                return null;
            }
            
            const gifBlob = await response.blob();
            
            // Кешируем в Cache API
            await this.cacheManager.cacheImage(gifUrl, gifBlob);
            
            // Кешируем в IndexedDB
            await this.imageCacheDB.saveImage(gifUrl, gifBlob, {
                url: gifUrl,
                postcardId: postcard.id,
                originalUrl: gifUrl,
                method: 'fetch',
                type: 'gif',
                mimeType: gifBlob.type,
                realSize: gifBlob.size
            });
            
            return gifBlob;
        } catch (error) {
            return null;
        }
    }

    /**
     * Альтернативный метод через fetch (если canvas tainted)
     */
    async cacheImageViaFetch(postcard, imageUrl) {
        try {
            // Пробуем загрузить через fetch с CORS
            const response = await fetch(imageUrl, {
                mode: 'cors',
                credentials: 'omit'
            });
            
            if (!response.ok) {
                return null;
            }
            
            const blob = await response.blob();
            
            // Кешируем в Cache API
            await this.cacheManager.cacheImage(imageUrl, blob);
            
            // Кешируем в IndexedDB
            await this.imageCacheDB.saveImage(imageUrl, blob, {
                url: imageUrl,
                postcardId: postcard.id,
                originalUrl: imageUrl,
                method: 'fetch-fallback'
            });
            
            return blob;
        } catch (error) {
            return null;
        }
    }
}
